import { useEffect, useRef } from 'react'

export function notificationsSupported() {
  return typeof window !== 'undefined' && 'Notification' in window
}

export async function requestNotificationPermission() {
  if (!notificationsSupported()) return 'unsupported'
  return Notification.requestPermission()
}

function fire(title, body) {
  if (!notificationsSupported() || Notification.permission !== 'granted') return
  new Notification(title, { body, icon: '/icons/icon-192.png' })
}

// Fires a browser notification when the current time matches a meal or
// supplement time in today's plan. Only works while this tab is open and in
// the foreground (or briefly backgrounded) - iOS Safari does not allow true
// background timers for installed web apps without server-sent Web Push, so
// this is a best-effort "app open around that time" reminder rather than a
// guaranteed background alert.
export function useDailyAlerts(dayPlan, enabled) {
  const firedTodayRef = useRef(new Set())
  const dayRef = useRef(null)

  useEffect(() => {
    if (!enabled || !dayPlan) return

    const todayKey = new Date().toDateString()
    if (dayRef.current !== todayKey) {
      firedTodayRef.current = new Set()
      dayRef.current = todayKey
    }

    const events = [
      ...dayPlan.meals.map((m) => ({ id: `meal-${m.key}`, time: m.time, title: m.label, body: m.items.join(', ') })),
      ...dayPlan.supplements.map((s) => ({
        id: `supp-${s.key}`,
        time: s.time,
        title: s.name,
        body: s.dose ? `${s.dose}${s.note ? ' — ' + s.note : ''}` : s.note || 'Take now',
      })),
    ]

    const check = () => {
      const now = new Date()
      const hhmm = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`
      for (const e of events) {
        if (e.time === hhmm && !firedTodayRef.current.has(e.id)) {
          fire(e.title, e.body)
          firedTodayRef.current.add(e.id)
        }
      }
    }

    check()
    const interval = setInterval(check, 20000)
    return () => clearInterval(interval)
  }, [dayPlan, enabled])
}
